import { type ChangeEvent, type CSSProperties, type ReactNode } from "react";
import { useTime } from "./core.js";

/**
 * Shared dense controls used across every game stage —
 * Label · BetInput · PrimaryCta · FairnessFooter · Wallet ·
 * MuteToggle · SectionHeader · Stat.
 *
 * Game-specific dense controls (e.g. MineCountPicker, StatusPill,
 * MultiplierDisplay) live in their own per-game Controls.tsx.
 *
 * Hoisted from games/pressure-plate/Controls.tsx so future games can
 * compose the same atoms without duplication. The pressure-plate version
 * is now a re-export shim for these primitives.
 */

// ---- Label ----------------------------------------------------------------
export function Label({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-ui)",
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 2,
        color: "var(--fg-3)",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

// ---- BetInput ------------------------------------------------------------
export function BetInput({
  amount = 0.001,
  currency = "BTC",
  onChange,
  disabled = false,
}: {
  amount?: number;
  currency?: string;
  onChange?: (v: number) => void;
  disabled?: boolean;
}) {
  const presets: Array<{ label: string; action: (a: number) => number }> = [
    { label: "½", action: (a) => a / 2 },
    { label: "2×", action: (a) => a * 2 },
    { label: "MAX", action: () => 1.0 },
    { label: "CLR", action: () => 0 },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, opacity: disabled ? 0.6 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <Label>Bet</Label>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--fg-3)",
            whiteSpace: "nowrap",
          }}
        >
          ${(amount * 65000).toFixed(2)}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          background: "var(--bg-2)",
          border: "1px solid var(--line)",
          borderRadius: 3,
        }}
      >
        <input
          type="text"
          value={amount.toFixed(8)}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const v = parseFloat(e.target.value);
            // Reject NaN, ±Infinity, and negatives at the input boundary so a
            // pasted "1e308" or "-1" never reaches deploy().
            if (!Number.isFinite(v) || v < 0) return;
            onChange?.(v);
          }}
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            fontFamily: "var(--font-mono)",
            fontSize: 16,
            color: "var(--fg-1)",
            padding: "10px 12px",
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 12px",
            borderLeft: "1px solid var(--line)",
            color: "var(--gold-bright)",
            fontFamily: "var(--font-display)",
            fontSize: 14,
            letterSpacing: 1,
          }}
        >
          {currency}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
        {presets.map((p) => (
          <button
            key={p.label}
            type="button"
            disabled={disabled}
            onClick={() => onChange?.(p.action(amount))}
            style={{
              height: 32,
              background: "var(--bg-2)",
              border: "1px solid var(--line)",
              borderRadius: 2,
              color: "var(--fg-2)",
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- PrimaryCta ----------------------------------------------------------
export function PrimaryCta({
  label = "DEPLOY",
  sub = null,
  kind = "gold",
  disabled = false,
  height = 60,
  full = true,
  big = false,
  onClick,
}: {
  label?: string;
  sub?: string | null;
  kind?: "gold" | "green" | "ghost";
  disabled?: boolean;
  height?: number;
  full?: boolean;
  big?: boolean;
  onClick?: () => void;
}) {
  const t = useTime();
  const pulse = 0.5 + 0.5 * Math.sin(t * 2.5);
  let bg: string;
  let fg: string;
  let edge: string;
  let glow: string;
  if (kind === "gold") {
    bg = "linear-gradient(180deg, var(--gold-bright) 0%, var(--gold) 50%, var(--gold-deep) 100%)";
    fg = "var(--gold-shadow)";
    edge = "var(--gold-deep)";
    glow = `0 0 ${20 + pulse * 12}px rgba(241,203,107,0.5)`;
  } else if (kind === "green") {
    bg = "linear-gradient(180deg, var(--green-bright) 0%, var(--green) 100%)";
    fg = "#0B1810";
    edge = "#2D6E48";
    glow = `0 0 ${20 + pulse * 12}px rgba(91,208,138,0.5)`;
  } else {
    bg = "var(--bg-2)";
    fg = "var(--fg-3)";
    edge = "var(--line)";
    glow = "none";
  }
  const btnStyle: CSSProperties = {
    position: "relative",
    width: full ? "100%" : "auto",
    minWidth: full ? 280 : undefined,
    height,
    background: disabled ? "var(--bg-2)" : bg,
    border: `1px solid ${disabled ? "var(--line)" : edge}`,
    borderRadius: 3,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    boxShadow: disabled ? "none" : `${glow}, var(--shadow-inset)`,
    color: disabled ? "var(--fg-3)" : fg,
    fontFamily: "var(--font-display)",
    letterSpacing: 2,
    textShadow: disabled ? "none" : "0 1px 0 rgba(255,255,255,0.2)",
    overflow: "hidden",
  };
  return (
    <button type="button" disabled={disabled} onClick={onClick} style={btnStyle}>
      <div
        style={{
          display: "flex",
          flexDirection: big ? "row" : "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          gap: big ? 16 : 2,
        }}
      >
        <span style={{ fontSize: big ? 28 : 20, lineHeight: 1 }}>{label}</span>
        {sub && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              letterSpacing: 0.5,
              fontWeight: 500,
              opacity: 0.85,
            }}
          >
            {sub}
          </span>
        )}
      </div>
      {!disabled && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
            borderRadius: 3,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              width: 90,
              left: `${((t * 28) % 220) - 50}%`,
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)",
              transform: "skewX(-20deg)",
            }}
          />
        </div>
      )}
    </button>
  );
}

// ---- FairnessFooter -----------------------------------------------------
// Slim version — only the green badge + verify CTA. The full hash data lives
// in each game's right-rail HashCard, so we don't duplicate it here.

export function FairnessFooter({ onVerify }: { onVerify?: () => void }) {
  return (
    <div
      className="pp-fairness-slim"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        background: "var(--bg-2)",
        border: "1px solid var(--line)",
        borderRadius: 3,
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        color: "var(--fg-3)",
        whiteSpace: "nowrap",
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          color: "var(--green-bright)",
          fontFamily: "var(--font-ui)",
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: 2,
          textTransform: "uppercase",
        }}
      >
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
          <path
            d="M2 6l3 3 5-6"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Provably Fair
      </div>
      <span style={{ width: 1, height: 14, background: "var(--line)" }} />
      <button
        type="button"
        onClick={onVerify}
        style={{
          background: "transparent",
          border: "none",
          cursor: onVerify ? "pointer" : "default",
          color: "var(--cyan)",
          textDecoration: "underline",
          textUnderlineOffset: 3,
          fontFamily: "var(--font-ui)",
          fontSize: 10,
          fontWeight: 600,
          padding: 0,
        }}
      >
        verify ↗
      </button>
    </div>
  );
}

// ---- Wallet & MuteToggle ------------------------------------------------
export function Wallet({ balance = 0.04231854 }: { balance?: number }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "6px 12px",
        background: "var(--bg-2)",
        border: "1px solid var(--line)",
        borderRadius: 3,
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "linear-gradient(135deg, var(--gold-bright), var(--gold-deep))",
          border: "1px solid var(--gold-deep)",
          display: "grid",
          placeItems: "center",
          fontFamily: "var(--font-display)",
          fontSize: 11,
          color: "var(--gold-shadow)",
        }}
      >
        ₿
      </div>
      <div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            color: "var(--fg-1)",
            lineHeight: 1,
            fontWeight: 500,
          }}
        >
          {balance.toFixed(8)}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "var(--fg-4)",
            marginTop: 2,
          }}
        >
          ${(balance * 65000).toFixed(2)}
        </div>
      </div>
    </div>
  );
}

export function MuteToggle({
  muted = false,
  onClick,
}: {
  muted?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={muted ? "Unmute sound" : "Mute sound"}
      style={{
        width: 36,
        height: 36,
        background: "var(--bg-2)",
        border: "1px solid var(--line)",
        borderRadius: 3,
        cursor: "pointer",
        color: muted ? "var(--red-bright)" : "var(--fg-2)",
        display: "grid",
        placeItems: "center",
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        {muted ? (
          <>
            <line x1="22" y1="9" x2="16" y2="15" />
            <line x1="16" y1="9" x2="22" y2="15" />
          </>
        ) : (
          <>
            <path d="M15.54 8.46a5 5 0 010 7.07" />
            <path d="M19.07 4.93a10 10 0 010 14.14" />
          </>
        )}
      </svg>
    </button>
  );
}

// ---- SectionHeader & Stat (sidebar primitives) ---------------------------
export function SectionHeader({ title, no }: { title: string; no: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 8,
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 2.5,
          color: "var(--gold-bright)",
          textTransform: "uppercase",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          color: "var(--fg-4)",
          letterSpacing: 1,
        }}
      >
        {no}
      </div>
    </div>
  );
}

export function Stat({ l, v, c }: { l: string; v: string | number; c?: string }) {
  return (
    <div
      style={{
        background: "rgba(17,21,31,0.7)",
        border: "1px solid var(--line)",
        borderRadius: 2,
        padding: "8px 10px",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 9,
          color: "var(--fg-3)",
          letterSpacing: 1.5,
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        {l}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 14,
          color: c ?? "var(--fg-1)",
          marginTop: 3,
          fontWeight: 600,
        }}
      >
        {v}
      </div>
    </div>
  );
}
